import React, { useState, useEffect, useMemo } from 'react';
import { useSocket } from '../../store/SocketContext';
import { RefreshCw, AlertTriangle, Download, Search, X, BarChart2, ZoomIn, Filter } from 'lucide-react';
import { useResultsFilter } from '../../hooks/useResultsFilter';
import { exportToCSV, exportToJSON, getExportFilename } from '../../utils/exportResults';
import { API_PATH, request } from '../../config/api';
import ResultsView from '../../components/activities/ResultsView';
import Tooltip from '../../components/ui/Tooltip';
import { useConfirm } from '../../store/ConfirmDialogContext';
import styles from './LiveResultsPanel.module.css';

const API = API_PATH;

function loadResults(eventId, activityId) {
    return request(`${API}/events/${eventId}/activities/${activityId}/results`)
        .then((r) => {
            if (!r.ok) {
                throw new Error(
                    r.status === 404 ? 'Activity not found' : r.status === 401 ? 'Session expired' : `Error ${r.status}`
                );
            }
            return r.json();
        })
        .then((data) => {
            if (!Array.isArray(data)) throw new Error('Invalid response format');
            return data.map((r) => {
                let answer = r.answer;
                if (typeof answer === 'string') {
                    try {
                        answer = JSON.parse(answer);
                    } catch {
                        answer = null;
                    }
                }
                return { ...r, answer };
            });
        });
}

export default function LiveResultsPanel({
    isOpen,
    onClose,
    eventId,
    activity,
    onParticipantsCountUpdate,
    onResponsesCountUpdate,
    toolbarOpen: toolbarOpenProp,
    onToolbarOpenChange,
}) {
    const socket = useSocket();
    const confirm = useConfirm();
    const [responses, setResponses] = useState([]);
    const [count, setCount] = useState(0);
    const [participantsCount, setParticipantsCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isRetrying, setIsRetrying] = useState(false);
    const [isExporting, setIsExporting] = useState(null);
    const [imageZoomOpen, setImageZoomOpen] = useState(false);
    const [internalToolbarOpen, setInternalToolbarOpen] = useState(false);
    const isToolbarControlled = toolbarOpenProp != null && typeof onToolbarOpenChange === 'function';
    const toolbarOpen = isToolbarControlled ? toolbarOpenProp : internalToolbarOpen;
    const setToolbarOpen = isToolbarControlled ? onToolbarOpenChange : setInternalToolbarOpen;

    const {
        searchTerm,
        setSearchTerm,
        sortBy,
        setSortBy,
        filteredResponses,
        resultCount,
        totalCount,
        clearFilters,
        hasActiveFilters,
    } = useResultsFilter(responses);

    useEffect(() => {
        onParticipantsCountUpdate?.(participantsCount);
    }, [participantsCount, onParticipantsCountUpdate]);

    useEffect(() => {
        onResponsesCountUpdate?.(count);
    }, [count, onResponsesCountUpdate]);

    useEffect(() => {
        if (!isOpen || !activity?.id) return;
        setResponses([]);
        setError(null);
        setIsLoading(true);
        loadResults(eventId, activity.id)
            .then((parsed) => {
                setResponses(parsed);
                setCount(parsed.length);
                setError(null);
            })
            .catch((err) => {
                setError(err.message || 'Failed to load results.');
                setResponses([]);
                setCount(0);
            })
            .finally(() => setIsLoading(false));
    }, [isOpen, activity?.id, eventId]);

    const handleRetry = () => {
        setIsRetrying(true);
        setError(null);
        loadResults(eventId, activity?.id)
            .then((parsed) => {
                setResponses(parsed);
                setCount(parsed.length);
            })
            .catch((err) => setError(err.message || 'Failed to load results.'))
            .finally(() => setIsRetrying(false));
    };

    const handleDeleteResponse = async (responseId) => {
        if (!eventId || !activity?.id) return;
        const ok = await confirm({
            title: 'Delete Response',
            message: 'Delete this response? This cannot be undone.',
            confirmText: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        try {
            const res = await request(
                `${API}/events/${eventId}/activities/${activity.id}/results/${responseId}`,
                { method: 'DELETE' }
            );
            if (!res.ok) throw new Error('Failed to delete');
            const parsed = await loadResults(eventId, activity.id);
            setResponses(parsed);
            setCount(parsed.length);
        } catch (err) {
            console.error(err);
            alert('Failed to delete response.');
        }
    };

    const handleApproveResponse = async (responseId) => {
        if (!eventId || !activity?.id) return;
        try {
            const res = await request(
                `${API}/events/${eventId}/activities/${activity.id}/results/${responseId}/approve`,
                { method: 'PATCH' }
            );
            if (!res.ok) throw new Error('Failed to approve');
            setResponses((prev) =>
                prev.map((r) =>
                    r.id != null && Number(r.id) === Number(responseId)
                        ? { ...r, answer: { ...(r.answer || {}), status: 'approved' } }
                        : r
                )
            );
        } catch (err) {
            console.error(err);
            alert('Failed to approve response.');
        }
    };

    const handleAdminReply = async (responseId, text) => {
        if (!eventId || !activity?.id) return;
        try {
            const res = await request(
                `${API}/events/${eventId}/activities/${activity.id}/results/${responseId}/reply`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ text: text.trim() }),
                }
            );
            if (!res.ok) throw new Error('Failed to add comment');
            setResponses((prev) =>
                prev.map((r) => {
                    if (r.id == null || Number(r.id) !== Number(responseId)) return r;
                    const replies = Array.isArray(r.answer?.replies) ? [...r.answer.replies] : [];
                    replies.push({ participant: 'Moderator', text: text.trim(), createdAt: new Date().toISOString(), role: 'admin' });
                    return { ...r, answer: { ...(r.answer || {}), replies } };
                })
            );
        } catch (err) {
            console.error(err);
            alert('Failed to add comment.');
        }
    };

    const handleHighlightQuestion = async (responseId, highlighted) => {
        if (!eventId || !activity?.id) return;
        try {
            const res = await request(
                `${API}/events/${eventId}/activities/${activity.id}/results/${responseId}/highlight`,
                {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ highlighted }),
                }
            );
            if (!res.ok) throw new Error('Failed to update highlight');
            setResponses((prev) =>
                prev.map((r) =>
                    r.id != null && Number(r.id) === Number(responseId)
                        ? { ...r, answer: { ...(r.answer || {}), highlighted } }
                        : r
                )
            );
        } catch (err) {
            console.error(err);
            alert('Failed to update highlight.');
        }
    };

    const handleDeleteReply = async (responseId, replyIndex) => {
        if (!eventId || !activity?.id) return;
        const ok = await confirm({
            title: 'Delete Reply',
            message: 'Delete this reply? This cannot be undone.',
            confirmText: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        try {
            const res = await request(
                `${API}/events/${eventId}/activities/${activity.id}/results/${responseId}/replies/${replyIndex}`,
                { method: 'DELETE' }
            );
            if (!res.ok) throw new Error('Failed to delete reply');
            const parsed = await loadResults(eventId, activity.id);
            setResponses(parsed);
            setCount(parsed.length);
        } catch (err) {
            console.error(err);
            alert('Failed to delete reply.');
        }
    };

    useEffect(() => {
        if (!socket || !isOpen || !activity?.id) return;
        socket.emit('admin:join', { eventId });
        const handleRoomUpdate = ({ count: c }) => setParticipantsCount(c);
        socket.on('room:update', handleRoomUpdate);
        // A socket drop can land at the exact moment a participant responds while this panel is
        // open; that broadcast is never replayed on reconnect, so the tally would silently drift
        // until the panel is closed and reopened. Re-sync from the server on every (re)connect.
        const handleConnect = () => {
            socket.emit('admin:join', { eventId });
            loadResults(eventId, activity.id)
                .then((parsed) => {
                    setResponses(parsed);
                    setCount(parsed.length);
                })
                .catch(() => {});
        };
        socket.on('connect', handleConnect);
        const handler = ({ activityId, responseId, answer, participant, createdAt }) => {
            if (Number(activityId) !== Number(activity.id)) return;
            let parsed = answer;
            if (typeof answer === 'string') {
                try {
                    parsed = JSON.parse(answer);
                } catch (e) {
                    console.error('Parse answer:', e);
                }
            }
            setResponses((prev) => {
                // Match by responseId, not participant name — see JoinPage.jsx's handleNewResponse
                // for why: matching by name would incorrectly merge two different Anonymous
                // participants' responses into one row, even though the DB stores them separately.
                const idx = responseId != null
                    ? prev.findIndex((r) => r.id != null && Number(r.id) === Number(responseId))
                    : -1;
                const newRow = { id: responseId, answer: parsed, participant, created_at: createdAt, reactions: {} };
                if (idx > -1) {
                    const next = [...prev];
                    next[idx] = { ...next[idx], ...newRow };
                    return next;
                }
                setCount((c) => c + 1);
                return [...prev, newRow];
            });
        };
        const handleResponseReaction = ({ activityId: aid, responseId, reactions }) => {
            if (Number(aid) !== Number(activity.id)) return;
            setResponses((prev) => prev.map((r) => (r.id != null && Number(r.id) === Number(responseId) ? { ...r, reactions } : r)));
        };
        const handleResponseUpdated = ({ activityId: aid, responseId, answer: rawAnswer }) => {
            if (Number(aid) !== Number(activity.id)) return;
            const answer = typeof rawAnswer === 'string' ? (() => { try { return JSON.parse(rawAnswer); } catch { return rawAnswer; } })() : rawAnswer;
            setResponses((prev) =>
                prev.map((r) => (r.id != null && Number(r.id) === Number(responseId) ? { ...r, answer } : r))
            );
        };
        socket.on('response:new', handler);
        socket.on('response:reaction', handleResponseReaction);
        socket.on('response:updated', handleResponseUpdated);
        return () => {
            socket.off('response:new', handler);
            socket.off('response:reaction', handleResponseReaction);
            socket.off('response:updated', handleResponseUpdated);
            socket.off('room:update', handleRoomUpdate);
            socket.off('connect', handleConnect);
        };
    }, [socket, isOpen, activity?.id, eventId]);

    const handleExportCSV = async () => {
        setIsExporting('csv');
        try {
            await new Promise((r) => setTimeout(r, 200));
            exportToCSV(responses, activity, getExportFilename(activity?.title, 'csv'));
        } finally {
            setIsExporting(null);
        }
    };

    const handleExportJSON = async () => {
        setIsExporting('json');
        try {
            await new Promise((r) => setTimeout(r, 200));
            exportToJSON(responses, activity, getExportFilename(activity?.title, 'json'));
        } finally {
            setIsExporting(null);
        }
    };

    if (!isOpen) return null;

    const content = useMemo(() => {
        const raw = activity?.content;
        if (raw == null) return null;
        if (typeof raw === 'object') return raw;
        if (typeof raw === 'string') {
            try {
                return JSON.parse(raw);
            } catch {
                return null;
            }
        }
        return null;
    }, [activity?.content]);

    const type = activity?.type;
    const title = activity?.title || 'Live Results';
    const imageUrl = content?.settings?.imageUrl ?? content?.settings?.image_url;
    const hasImage = imageUrl && String(imageUrl).trim();

    const isAudienceQaModeration = type === 'audience-qa' && content?.settings?.moderation === true;
    const { approvedResponses, pendingResponses } = useMemo(() => {
        if (!isAudienceQaModeration) return { approvedResponses: filteredResponses, pendingResponses: [] };
        const approved = filteredResponses.filter((r) => !r.answer || r.answer.status !== 'pending');
        const pending = filteredResponses.filter((r) => r.answer && r.answer.status === 'pending');
        return { approvedResponses: approved, pendingResponses: pending };
    }, [isAudienceQaModeration, filteredResponses]);

    const displayResponses = isAudienceQaModeration ? approvedResponses : filteredResponses;
    const displayCount = displayResponses.length;

    return (
        <div className={styles.panel}>
            {!isToolbarControlled && (
                <Tooltip title={toolbarOpen ? 'Hide filter & export' : 'Filter & export'} placement="bottom">
                    <button
                        type="button"
                        className={`${styles.toolbarToggleBtn} ${toolbarOpen ? styles.toolbarToggleBtnActive : ''}`}
                        onClick={() => setToolbarOpen((o) => !o)}
                        aria-label={toolbarOpen ? 'Hide filter and export' : 'Filter and export'}
                        aria-expanded={toolbarOpen}
                    >
                        <Filter size={18} />
                        {hasActiveFilters && <span className={styles.toolbarToggleBadge} aria-hidden />}
                    </button>
                </Tooltip>
            )}

            {toolbarOpen && ( <>
                <div className={styles.toolbar}>
                    <div className={styles.searchWrap}>
                        <Search size={16} className={styles.searchIcon} aria-hidden />
                        <input
                            type="search"
                            placeholder="Search by participant name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={styles.searchInput}
                            aria-label="Search responses"
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                className={styles.searchClear}
                                onClick={() => setSearchTerm('')}
                                aria-label="Clear search"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                    <div className={styles.toolbarRight}>
                        <Tooltip title="Sort by" placement="bottom">
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className={styles.sortSelect}
                                aria-label="Sort responses"
                            >
                            <option value="recent">Newest first</option>
                            <option value="oldest">Oldest first</option>
                            <option value="name">Name (A–Z)</option>
                            </select>
                        </Tooltip>
                        <Tooltip title="Export as CSV" placement="bottom">
                            <button
                                type="button"
                                className={styles.exportBtn}
                                onClick={handleExportCSV}
                                disabled={responses.length === 0 || isExporting}
                            >
                                <Download size={14} />
                                CSV
                            </button>
                        </Tooltip>
                        <Tooltip title="Export as JSON" placement="bottom">
                            <button
                                type="button"
                                className={styles.exportBtn}
                                onClick={handleExportJSON}
                                disabled={responses.length === 0 || isExporting}
                            >
                                <Download size={14} />
                                JSON
                            </button>
                        </Tooltip>
                        {hasActiveFilters && (
                            <Tooltip title="Clear filters" placement="bottom">
                                <button type="button" className={styles.clearFiltersBtn} onClick={clearFilters}>
                                    Clear
                                </button>
                            </Tooltip>
                        )}
                    </div>
                </div>
                  <div className={styles.resultsSummary}>
                  <span className={styles.resultsCount}>
                      {resultCount} of {totalCount} response{totalCount !== 1 ? 's' : ''}
                  </span>
                  {hasActiveFilters && <span className={styles.filterTag}>Filtered</span>}
              </div></>
            )}

          

            {error && (
                <div className={styles.errorCard} role="alert">
                    <div className={styles.errorHeader}>
                        <AlertTriangle size={16} />
                        Failed to load results
                    </div>
                    <p className={styles.errorMessage}>{error}</p>
                    <button type="button" className={styles.retryBtn} onClick={handleRetry} disabled={isRetrying}>
                        {isRetrying ? (
                            <>
                                <RefreshCw size={14} className={styles.spinner} />
                                Retrying…
                            </>
                        ) : (
                            <>
                                <RefreshCw size={14} />
                                Retry
                            </>
                        )}
                    </button>
                </div>
            )}

            {isLoading && !error && (
                <div className={styles.loadingState}>
                    <div className={styles.loadingSpinner} />
                    <span>Loading results…</span>
                </div>
            )}

            {!isLoading && !error && (
                <div className={styles.body}>
                    <article className={`${styles.activityCard} ${hasImage ? styles.activityCardWithImage : ''}`}>
                        {hasImage ? (
                            <>
                                <div className={styles.cardInner}>
                                    <div className={styles.cardMedia}>
                                        <img
                                            src={imageUrl}
                                            alt=""
                                            className={styles.cardMediaImg}
                                            decoding="async"
                                            loading="lazy"
                                        />
                                        <div className={styles.cardMediaOverlay}>
                                            <Tooltip title="Zoom image" placement="top">
                                                <button
                                                    type="button"
                                                    className={styles.cardZoomBtn}
                                                    onClick={() => setImageZoomOpen(true)}
                                                    aria-label="Zoom image"
                                                >
                                                    <ZoomIn size={18} />
                                                </button>
                                            </Tooltip>
                                        </div>
                                    </div>
                                    <div className={styles.cardRight}>
                                        <div className={styles.cardHeader}>
                                            <span className={styles.cardResponseCount}>
                                                {resultCount} of {totalCount} response{totalCount !== 1 ? 's' : ''}
                                            </span>
                                            {hasActiveFilters && <span className={styles.filterTag}>Filtered</span>}
                                        </div>
                                        <div className={styles.cardContent}>
                                            {content?.question && (
                                                <section className={styles.cardSection}>
                                                    <span className={styles.sectionLabel}>Question</span>
                                                    <p className={styles.questionText}>{content.question}</p>
                                                </section>
                                            )}
                                            {content?.settings?.descText?.trim() && (
                                                <section className={styles.cardSection}>
                                                    <span className={styles.sectionLabel}>Description</span>
                                                    <p className={styles.description}>{content.settings.descText.trim()}</p>
                                                </section>
                                            )}
                                        </div>
                                        {isAudienceQaModeration && pendingResponses.length > 0 && (
                                            <div className={styles.cardResults}>
                                                <span className={styles.sectionLabel}>Pending approval</span>
                                                <ul className={styles.pendingList}>
                                                    {pendingResponses.map((r) => (
                                                        <li key={r.id} className={styles.pendingItem}>
                                                            <span className={styles.pendingText}>{r.answer?.text || '—'}</span>
                                                            <span className={styles.pendingMeta}>{r.participant || 'Anonymous'}</span>
                                                            <button
                                                                type="button"
                                                                className={styles.approveBtn}
                                                                onClick={() => handleApproveResponse(r.id)}
                                                            >
                                                                Approve
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        <div className={styles.cardResults}>
                                            <span className={styles.sectionLabel}>Results</span>
                                            <div className={styles.results}>
                                                {displayCount > 0 ? (
                                                    <ResultsView
                                                        type={type}
                                                        responses={displayResponses}
                                                        content={content}
                                                        isParticipantView={false}
                                                        onDeleteResponse={(type === 'open-text' || type === 'audience-qa') ? handleDeleteResponse : undefined}
                                                        onAdminReply={(type === 'audience-qa') ? handleAdminReply : undefined}
                                                        onHighlightQuestion={(type === 'audience-qa') ? handleHighlightQuestion : undefined}
                                                        onDeleteReply={(type === 'audience-qa') ? handleDeleteReply : undefined}
                                                    />
                                                ) : (
                                                    <div className={styles.noResults}>
                                                        {hasActiveFilters ? 'No responses match your filters' : (pendingResponses.length > 0 ? 'No approved questions yet' : 'No responses yet')}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {imageZoomOpen && (
                                    <div className={styles.imageZoomOverlay} onClick={() => setImageZoomOpen(false)} role="dialog" aria-modal="true" aria-label="Image zoom">
                                        <div className={styles.imageZoomModal} onClick={(e) => e.stopPropagation()}>
                                            <button
                                                type="button"
                                                className={styles.imageZoomClose}
                                                onClick={() => setImageZoomOpen(false)}
                                                aria-label="Close zoom"
                                            >
                                                <X size={18} />
                                            </button>
                                            <img src={imageUrl} alt="" className={styles.imageZoomImg} />
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {/* <div className={styles.cardHeader}>
                                    <span className={styles.cardResponseCount}>
                                        {resultCount} of {totalCount} response{totalCount !== 1 ? 's' : ''}
                                    </span>
                                    {hasActiveFilters && <span className={styles.filterTag}>Filtered</span>}
                                </div> */}
                                {(content?.question || content?.settings?.descText?.trim()) && (
                                    <div className={styles.cardContent}>
                                        {content?.question && (
                                            <section className={styles.cardSection}>
                                                <span className={styles.sectionLabel}>Question</span>
                                                <p className={styles.questionText}>{content.question}</p>
                                            </section>
                                        )}
                                        {content?.settings?.descText?.trim() && (
                                            <section className={styles.cardSection}>
                                                <span className={styles.sectionLabel}>Description</span>
                                                <p className={styles.description}>{content.settings.descText.trim()}</p>
                                            </section>
                                        )}
                                    </div>
                                )}
                                {isAudienceQaModeration && pendingResponses.length > 0 && (
                                    <div className={styles.cardResults}>
                                        <span className={styles.sectionLabel}>Pending approval</span>
                                        <ul className={styles.pendingList}>
                                            {pendingResponses.map((r) => (
                                                <li key={r.id} className={styles.pendingItem}>
                                                    <span className={styles.pendingText}>{r.answer?.text || '—'}</span>
                                                    <span className={styles.pendingMeta}>{r.participant || 'Anonymous'}</span>
                                                    <button
                                                        type="button"
                                                        className={styles.approveBtn}
                                                        onClick={() => handleApproveResponse(r.id)}
                                                    >
                                                        Approve
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <div className={styles.cardResults}>
                                    <span className={styles.sectionLabel}>Results</span>
                                    <div className={styles.results}>
                                        {displayCount > 0 ? (
                                            <ResultsView
                                                type={type}
                                                responses={displayResponses}
                                                content={content}
                                                isParticipantView={false}
                                                onDeleteResponse={(type === 'open-text' || type === 'audience-qa') ? handleDeleteResponse : undefined}
                                                onAdminReply={(type === 'audience-qa') ? handleAdminReply : undefined}
                                                onHighlightQuestion={(type === 'audience-qa') ? handleHighlightQuestion : undefined}
                                                onDeleteReply={(type === 'audience-qa') ? handleDeleteReply : undefined}
                                            />
                                        ) : (
                                            <div className={styles.noResults}>
                                                {hasActiveFilters ? 'No responses match your filters' : (pendingResponses.length > 0 ? 'No approved questions yet' : 'No responses yet')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </article>
                </div>
            )}
        </div>
    );
}
